import React, { useState, useMemo } from "react";

import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";

import {
  Button,
  CssBaseline,
  TextField,
  Grid,
  Typography,
  InputAdornment,
  IconButton,
  Link,
  Paper,
} from "@material-ui/core";

import {
  LockOutlined,
  MailOutline,
  PersonOutline,
  Visibility,
  VisibilityOff,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import LupaRH from "../../components/LupaRH";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
  },
  leftSide: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#ffffff",
    backgroundImage:
      "radial-gradient(circle at 90% 8%, rgba(45,212,191,0.10), transparent 42%), radial-gradient(circle at 8% 96%, rgba(13,148,136,0.08), transparent 46%)",
    position: "relative",
    overflow: "hidden",
  },
  leftStars: { position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none" },
  leftStar: {
    position: "absolute",
    borderRadius: "50%",
    backgroundColor: "#0D9488",
    animationName: "$twinkleSoft",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
  },
  paper: {
    margin: theme.spacing(3, 4),
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: 400,
    position: "relative",
    zIndex: 1,
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  logoImage: { height: 56, width: 56, flexShrink: 0, marginRight: 12 },
  logoTextConsig: {
    color: "#031f42",
    fontSize: "1.8rem",
    fontWeight: 900,
    letterSpacing: 1.5,
  },
  logoTextTech: {
    color: "#0D9488",
    fontSize: "1.4rem",
    fontWeight: 900,
    letterSpacing: 1.5,
  },
  title: { fontSize: "1.9rem", fontWeight: 800, color: "#031f42", lineHeight: 1.1 },
  titleBar: {
    display: "block",
    width: 52,
    height: 4,
    borderRadius: 4,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    background: "linear-gradient(90deg, #0D9488, #14b8a6)",
  },
  subtitle: { color: "#6b7280", fontSize: "0.95rem", marginBottom: theme.spacing(2) },
  form: { width: "100%" },
  submit: {
    margin: theme.spacing(2, 0, 1),
    background: "linear-gradient(135deg, #0D9488 0%, #14b8a6 100%)",
    color: "#fff",
    padding: "12px 0",
    borderRadius: 8,
    textTransform: "none",
    fontSize: "1rem",
    fontWeight: "bold",
    boxShadow: "0 6px 18px rgba(13,148,136,0.35)",
    "&:hover": { background: "linear-gradient(135deg, #0f766e 0%, #27b866 100%)" },
  },
  input: {
    "& .MuiOutlinedInput-root": { borderRadius: 8, backgroundColor: "#fbfbfb" },
    "& input:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px #fbfbfb inset !important",
      WebkitTextFillColor: "#333 !important",
    },
  },

  rightSide: {
    backgroundColor: "#04101c",
    backgroundImage:
      "radial-gradient(circle at 50% 45%, #0c2340 0%, #071829 55%, #040d17 100%)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    color: "#fff",
    position: "relative",
    overflow: "hidden",
  },
  stars: { position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none" },
  star: {
    position: "absolute",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    boxShadow: "0 0 6px rgba(255,255,255,0.7)",
    animationName: "$twinkle",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
  },
  rightContent: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: theme.spacing(7),
  },
  galaxyTitle: {
    fontSize: "2.6rem",
    fontWeight: 900,
    letterSpacing: 0.5,
    lineHeight: 1.05,
    textAlign: "center",
  },
  galaxyAccent: { color: "#14b8a6" },
  galaxyTagline: {
    color: "#7ee0d4",
    marginTop: theme.spacing(1.5),
    fontWeight: 500,
    fontSize: "1.05rem",
    textAlign: "center",
    maxWidth: 420,
  },
  galaxyWrap: {
    position: "relative",
    width: 520,
    height: 520,
    marginTop: theme.spacing(1),
    maxWidth: "82vw",
    maxHeight: "66vh",
  },
  galaxyHaze: {
    position: "absolute",
    inset: "10%",
    borderRadius: "50%",
    background:
      "radial-gradient(ellipse at center, rgba(126,224,212,0.12) 0%, rgba(126,224,212,0.05) 38%, transparent 72%)",
    zIndex: 1,
    filter: "blur(8px)",
  },
  galaxyCore: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%,-50%)",
    width: 74,
    height: 74,
    borderRadius: "50%",
    background:
      "radial-gradient(circle at 45% 40%, #fffde7 0%, #ffe680 20%, #7fc0ff 58%, #1e63d6 100%)",
    zIndex: 3,
    animationName: "$pulseStar",
    animationDuration: "3.5s",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
  },
  galaxy: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    animationName: "$spin",
    animationDuration: "70s",
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
  },
  galaxyDot: {
    position: "absolute",
    borderRadius: "50%",
    transform: "translate(-50%,-50%)",
  },

  "@keyframes spin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
  "@keyframes twinkle": {
    "0%, 100%": { opacity: 0.15 },
    "50%": { opacity: 1 },
  },
  "@keyframes twinkleSoft": {
    "0%, 100%": { opacity: 0.08 },
    "50%": { opacity: 0.32 },
  },
  "@keyframes pulseStar": {
    "0%, 100%": { boxShadow: "0 0 34px 8px rgba(120,180,255,0.55)" },
    "50%": { boxShadow: "0 0 62px 18px rgba(150,200,255,0.9)" },
  },
}));

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Muito Curto!")
    .max(50, "Muito Longo!")
    .required("Obrigatório"),
  password: Yup.string().min(5, "Muito Curta!").max(50, "Muito Longa!"),
  email: Yup.string().email("E-mail inválido").required("Obrigatório"),
});

const SignUp = () => {
  const classes = useStyles();
  const history = useHistory();

  const initialState = { name: "", email: "", password: "" };
  const [showPassword, setShowPassword] = useState(false);
  const [user] = useState(initialState);

  const handleSignUp = async (values) => {
    try {
      await api.post("/auth/signup", values);
      toast.success(i18n.t("signup.toasts.success"));
      history.push("/login");
    } catch (err) {
      toastError(err);
    }
  };

  const stars = useMemo(
    () =>
      Array.from({ length: 130 }).map(() => ({
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: Math.random() * 2 + 1,
        delay: Math.random() * 4,
        dur: Math.random() * 3 + 2,
      })),
    []
  );

  const leftStars = useMemo(
    () =>
      Array.from({ length: 18 }).map(() => ({
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: Math.random() * 2 + 1,
        delay: Math.random() * 4,
        dur: Math.random() * 3 + 2,
      })),
    []
  );

  const galaxy = useMemo(() => {
    const pts = [];
    const pick = () => (Math.random() > 0.5 ? "#7ee0d4" : "#eafff2");

    for (let i = 0; i < 150; i++) {
      const r = Math.pow(Math.random(), 2) * 16;
      const ang = Math.random() * Math.PI * 2;
      pts.push({
        x: 50 + Math.cos(ang) * r,
        y: 50 + Math.sin(ang) * r,
        size: Math.random() * 1.6 + 0.6,
        op: Math.random() * 0.5 + 0.5,
        color: Math.random() > 0.6 ? "#fff6cf" : pick(),
      });
    }

    const arms = 4;
    const perArm = 120;
    for (let a = 0; a < arms; a++) {
      for (let i = 0; i < perArm; i++) {
        const t = (i / perArm) * Math.PI * 3.4;
        const spread = (Math.random() - 0.5) * (5 + t * 1.2);
        const r = 5 + t * 4 + spread;
        const ang = t + a * ((2 * Math.PI) / arms) + (Math.random() - 0.5) * 0.12;
        pts.push({
          x: 50 + Math.cos(ang) * r,
          y: 50 + Math.sin(ang) * r,
          size: Math.random() * 1.8 + 0.6,
          op: Math.random() * 0.55 + 0.35,
          color: pick(),
        });
      }
    }

    for (let i = 0; i < 140; i++) {
      const r = Math.random() * 48;
      const ang = Math.random() * Math.PI * 2;
      pts.push({
        x: 50 + Math.cos(ang) * r,
        y: 50 + Math.sin(ang) * r,
        size: Math.random() * 1.2 + 0.4,
        op: Math.random() * 0.3 + 0.12,
        color: pick(),
      });
    }

    return pts;
  }, []);

  return (
    <Grid container className={classes.root}>
      <CssBaseline />

      {}
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={0} square className={classes.leftSide}>
        <div className={classes.leftStars}>
          {leftStars.map((s, i) => (
            <span
              key={i}
              className={classes.leftStar}
              style={{
                top: `${s.top}%`,
                left: `${s.left}%`,
                width: s.size,
                height: s.size,
                animationDelay: `${s.delay}s`,
                animationDuration: `${s.dur}s`,
              }}
            />
          ))}
        </div>

        <div className={classes.paper}>
          <div className={classes.logoContainer}>
            <LupaRH className={classes.logoImage} cor="#0d9488" />
            <Typography variant="h6" style={{ lineHeight: 0.95, display: "flex", flexDirection: "column", justifyContent: "center" }} translate="no">
              <span className={classes.logoTextConsig}>I.A</span>
              <span className={classes.logoTextTech} style={{ fontSize: "0.95rem", letterSpacing: 0.4 }}>Recursos Humanos</span>
            </Typography>
          </div>

          <Typography component="h1" className={classes.title}>
            Cadastre-se
          </Typography>
          <span className={classes.titleBar} />
          <Typography className={classes.subtitle}>
            Crie sua conta e comece a usar em segundos.
          </Typography>

          <Formik
            initialValues={user}
            enableReinitialize={true}
            validationSchema={UserSchema}
            onSubmit={(values, actions) => {
              setTimeout(() => {
                handleSignUp(values);
                actions.setSubmitting(false);
              }, 400);
            }}
          >
            {({ touched, errors, isSubmitting }) => (
              <Form className={classes.form}>
                <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, color: "#333" }}>
                  Nome
                </Typography>
                <Field
                  as={TextField}
                  className={classes.input}
                  variant="outlined"
                  margin="dense"
                  required
                  fullWidth
                  id="name"
                  name="name"
                  placeholder="Seu nome completo"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  autoComplete="name"
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline style={{ color: "#999" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Typography variant="subtitle2" style={{ fontWeight: 600, marginTop: 8, marginBottom: 8, color: "#333" }}>
                  E-mail
                </Typography>
                <Field
                  as={TextField}
                  className={classes.input}
                  variant="outlined"
                  margin="dense"
                  required
                  fullWidth
                  id="email"
                  name="email"
                  placeholder="seuemail@exemplo.com"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  autoComplete="email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutline style={{ color: "#999" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Typography variant="subtitle2" style={{ fontWeight: 600, marginTop: 8, marginBottom: 8, color: "#333" }}>
                  Senha
                </Typography>
                <Field
                  as={TextField}
                  className={classes.input}
                  variant="outlined"
                  margin="dense"
                  required
                  fullWidth
                  name="password"
                  id="password"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  type={showPassword ? "text" : "password"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined style={{ color: "#999" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((e) => !e)}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  className={classes.submit}
                  disabled={isSubmitting}
                >
                  Cadastrar
                </Button>

                <Grid container justifyContent="center" style={{ marginTop: 16 }}>
                  <Grid item>
                    <Link href="#" variant="body2" component={RouterLink} to="/login" style={{ color: "#0D9488", fontWeight: "bold" }}>
                      Já tem uma conta? Entrar
                    </Link>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </div>
      </Grid>

      {}
      <Grid item xs={false} sm={4} md={7} className={classes.rightSide}>
        <div className={classes.stars}>
          {stars.map((s, i) => (
            <span
              key={i}
              className={classes.star}
              style={{
                top: `${s.top}%`,
                left: `${s.left}%`,
                width: s.size,
                height: s.size,
                animationDelay: `${s.delay}s`,
                animationDuration: `${s.dur}s`,
              }}
            />
          ))}
        </div>

        <div className={classes.rightContent}>
          <Typography component="h1" className={classes.galaxyTitle} translate="no">
            Junte-se ao <span className={classes.galaxyAccent}>I.A Recursos Humanos</span>
          </Typography>
          <Typography className={classes.galaxyTagline}>
            Mais de 6 mil contratos fechados. Chegou a sua vez!
          </Typography>

          <div className={classes.galaxyWrap}>
            <div className={classes.galaxyHaze} />
            <div className={classes.galaxyCore} />
            <div className={classes.galaxy}>
              {galaxy.map((p, i) => (
                <span
                  key={i}
                  className={classes.galaxyDot}
                  style={{
                    top: `${p.y}%`,
                    left: `${p.x}%`,
                    width: p.size,
                    height: p.size,
                    opacity: p.op,
                    backgroundColor: p.color,
                    boxShadow: `0 0 4px ${p.color}`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </Grid>
    </Grid>
  );
};

export default SignUp;
